/* eslint-disable react-portal/no-react-router-use-navigate */
import { i18n } from '@lingui/core';
import { t, Trans } from '@lingui/macro';
import { Box, Typography } from '@material-ui/core';
import { forEach, pick } from 'lodash';
import { useCallback, useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useGlobalDataService } from '../../services/ApiDataService';

import useSession, { useUserId } from '../../stores/SessionStore';
import { USE_FORM_DEFAULT_OPTIONS } from '../../utilities/FormUtilities';
import { useRequestState } from '../../utilities/Hooks';
import { isBadRequestError, rethrowError } from '../../utilities/RequestUtilities';
import NgpPasswordField from '../form/NgpPasswordField';
import { RowFlexLayout } from '../layout';
import PasswordRequirements from '../pages-admin/users/PasswordRequirements';
import { CONFIRM_PASSWORD_FIELD, PASSWORD_FIELD } from '../pages-admin/users/UserConstants';
import { SSO_LOGIN_REDIRECT } from './AuthConstants';
import AuthPanel from './AuthPanel';
import AuthProgressButton from './AuthProgressButton';
import usePostLoginRedirect from './UsePostLoginRedirect';
import useEnvironment from '../../stores/EnvironmentStore';

export default function ChangePasswordPage() {
    const [session, { refresh }] = useSession();
    const [environment] = useEnvironment();
    const navigate = useNavigate();

    const { navigateToLastLocation } = usePostLoginRedirect();
    const [userId] = useUserId();
    const changePassword = session?.userInfo?.user?.changePassword === true;
    const formMethods = useForm(USE_FORM_DEFAULT_OPTIONS);
    const password = formMethods.watch(PASSWORD_FIELD);

    const { handler, success, isLoading, error } = useChangePasswordHandler({
        userId,
        formMethods,
    });

    const focusedRef = useRef(false);
    const setFocus = (el) => {
        if (el && !focusedRef.current) {
            el.focus();
            focusedRef.current = true;
        }
    };

    useEffect(() => {
        if (!session.initialized) {
            refresh(environment);
            return; // wait for session initialization
        }

        if (!userId) navigate(SSO_LOGIN_REDIRECT);
        if (!changePassword || success) navigateToLastLocation();
    }, [changePassword, navigate, navigateToLastLocation, refresh, session, success, userId, environment]);

    return (
        <AuthPanel loading={!session.initialized}>
            <FormProvider {...formMethods}>
                <form onSubmit={formMethods.handleSubmit(handler)}>
                    <Typography variant="h6">
                        <Trans>Change Your Password</Trans>
                    </Typography>

                    <Typography variant="caption">
                        <Trans>You must change your password before you can log in</Trans>
                    </Typography>

                    <Box my={3}>
                        <PasswordRequirements noSpacing />
                    </Box>

                    <NgpPasswordField
                        fieldName={PASSWORD_FIELD}
                        label={<Trans>Password</Trans>}
                        disabled={isLoading}
                        required
                        inputRef={setFocus}
                        inputProps={{ size: 65, maxLength: 65, id: 'input_2' }}
                    />

                    <Box mt={1}>
                        <NgpPasswordField
                            fieldName={CONFIRM_PASSWORD_FIELD}
                            label={<Trans>Confirm Password</Trans>}
                            disabled={isLoading}
                            required
                            inputProps={{ size: 65, maxLength: 65, id: 'input_3' }}
                            fieldRules={{
                                validate: {
                                    passwordsEqual: (value) =>
                                        value !== password ? i18n._(t`Passwords do not match`) : true,
                                },
                            }}
                        />
                    </Box>

                    {error && (
                        <Typography variant="subtitle2" color="error" component={Box} my={3}>
                            <Trans>An error occurred trying to change your password</Trans>
                        </Typography>
                    )}

                    <RowFlexLayout mt={4} hRight>
                        <AuthProgressButton loading={isLoading} type="submit">
                            <Trans>Save</Trans>
                        </AuthProgressButton>
                    </RowFlexLayout>
                </form>
            </FormProvider>
        </AuthPanel>
    );
}

function useChangePasswordHandler({ userId, formMethods }) {
    const dataService = useGlobalDataService();
    const [, sessionActions] = useSession();
    const [environment] = useEnvironment();
    const {
        data,
        isLoading,
        error,
        methods: { onInit, onLoad, onError },
    } = useRequestState();

    const { setError } = formMethods;

    const handler = useCallback(
        async (formData) => {
            try {
                onInit();

                const postData = { ...pick(formData, ['password', 'confirmPassword']), userId };

                await dataService.put(`/ipm/v1/admin/userpasswords/${userId}`, postData, null, null, rethrowError);

                await sessionActions.refresh(environment);

                onLoad(true);
            } catch (e) {
                if (isBadRequestError(e)) {
                    const validations = e?.responseBody?.validations;

                    if (validations) {
                        forEach(validations, ({ message }, field) => setError(field, { message }));
                        onLoad(false);
                    } else {
                        onError(true);
                    }
                } else {
                    // eslint-disable-next-line no-console
                    console.error('Failed to change password with unknown error. Error:', e);
                    onError(true);
                }
            }
        },
        [dataService, onError, onInit, onLoad, setError, userId, sessionActions, environment]
    );

    return { handler, isLoading, error, success: data === true };
}
